// @flow

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { Icon } from '@blueprintjs/core';
import { ResizableSplit } from '../../ai-reports';
import { ReportChatWrapper } from '../../report-chat';
import { ChatWindow, PromptFooter, usePromptChatController, usePromptChatState, OrganisationContext } from '../../ai-prompt-management';
import { useReportRenderContext } from '../../../contexts/report-render-context';
import { fetchChatThreads } from '../../../actions/chatThreads';
import ReportContent from './ReportContent';
import * as Selectors from '../../../selectors';
import { ORG_HAS_STAFF_DETECTION, DISABLE_AGE_GENDER } from '../../../constants/features';

const PLACEHOLDER = 'Ask me about this report...';

// Styled components for the layout
// Use negative margins to counteract Layout's 2rem padding, allowing edge-to-edge content
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: calc(100% + 4rem); /* expand to fill Layout padding */
  margin: 0 -2rem; /* pull out to edges */
`;

const SplitArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  padding: 0 2rem; /* Match PanelFooter horizontal padding */
`;

const ChatContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const FooterArea = styled.div`
  flex: 0 0 auto;
  width: 100%;
  padding: 0.5rem 2rem; /* Match SplitArea horizontal padding */
  margin-top: 0.6rem; /* Match spacing between report widgets */
  border-top: 1px solid var(--panel-border, #ddd);
  border-radius: 12px;

  @keyframes footer-glow {
    0% {
      background-color: transparent;
      box-shadow: none;
      border-color: var(--panel-border, #ddd);
    }
    20% {
      background-color: rgba(59, 130, 246, 0.12);
      box-shadow:
        0 0 16px 6px rgba(59, 130, 246, 0.25),
        0 0 36px 14px rgba(59, 130, 246, 0.12),
        0 0 60px 24px rgba(59, 130, 246, 0.04);
      border-color: rgba(59, 130, 246, 0.3);
    }
    60% {
      background-color: rgba(59, 130, 246, 0.12);
      box-shadow:
        0 0 16px 6px rgba(59, 130, 246, 0.25),
        0 0 36px 14px rgba(59, 130, 246, 0.12),
        0 0 60px 24px rgba(59, 130, 246, 0.04);
      border-color: rgba(59, 130, 246, 0.3);
    }
    100% {
      background-color: transparent;
      box-shadow: none;
      border-color: var(--panel-border, #ddd);
    }
  }

  &.shine {
    animation: footer-glow 2s ease-in-out 1;
  }
`;

const ToggleChatButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--primary);
  font-size: 0.8em;
  padding: 0.25rem 0;
  margin: 0 auto 0.15rem;

  &:hover {
    color: var(--primary-hover);
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

// Hook to manage chat prompt state (must be used inside ReportChatWrapper)
const useChatControls = (inputRef: { current: ?HTMLTextAreaElement }, onFirstInteraction: () => void) => {
  const [prompt, setPrompt] = useState('');
  const controller = usePromptChatController();
  const state = usePromptChatState();
  const reportContext = useReportRenderContext();
  
  const isReportComplete = reportContext?.isReportComplete ?? false;
  const loadingWidgetCount = reportContext?.loadingWidgetCount ?? 0;
  const activeWidgetCount = reportContext?.activeWidgetCount ?? 0;
  
  const sendDisabled = state.isStreaming || !isReportComplete;
  const completedCount = activeWidgetCount - loadingWidgetCount;
  const completedPercent = Math.round(completedCount / activeWidgetCount * 100);
  const loadingStatusText = !isReportComplete 
    ? `Loading Report: ${completedPercent}%`
    : undefined;

  const handleSend = useCallback(async () => {
    if (!prompt.trim() || sendDisabled) return;
    onFirstInteraction();
    await controller.send(prompt);
    setPrompt('');
    if (inputRef?.current) inputRef.current.focus();
  }, [prompt, sendDisabled, controller, inputRef, onFirstInteraction]);

  const onKeyDown = useCallback((e: SyntheticKeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return {
    prompt,
    setPrompt,
    onKeyDown,
    isStreaming: state.isStreaming,
    handleSend,
    sendDisabled,
    loadingStatusText,
    resetController: controller.reset,
    isReportLoading: !isReportComplete,
  };
};

// Prompt footer component that uses the chat context
const PromptFooterWithContext = ({
  inputRef,
  onFirstInteraction,
  controllerResetRef,
  chatMinimized,
  onToggleMinimize,
  onExpandChat,
  footerRef,
}: {
  inputRef: { current: ?HTMLTextAreaElement },
  onFirstInteraction: () => void,
  controllerResetRef: { current: ?Function },
  chatMinimized: boolean,
  onToggleMinimize: () => void,
  onExpandChat: () => void,
  footerRef: { current: ?HTMLDivElement },
}): React$Node => {
  const controls = useChatControls(inputRef, onFirstInteraction);
  const wasLoadingRef = useRef(controls.isReportLoading);

  // Expose reset method to parent via ref
  useEffect(() => {
    if (controllerResetRef) {
      controllerResetRef.current = controls.resetController;
    }
  }, [controls.resetController, controllerResetRef]);

  // Trigger shine when report finishes loading
  useEffect(() => {
    if (wasLoadingRef.current && !controls.isReportLoading) {
      const el = footerRef.current;
      if (el) {
        el.classList.remove('shine');
        void el.offsetWidth;
        el.classList.add('shine');
        const onEnd = () => el.classList.remove('shine');
        el.addEventListener('animationend', onEnd, { once: true });
      }
    }
    wasLoadingRef.current = controls.isReportLoading;
  }, [controls.isReportLoading, footerRef]);

  return (
    <>
      <ToggleChatButton onClick={onToggleMinimize}>
        <Icon icon={chatMinimized ? 'chevron-up' : 'chevron-down'} iconSize={12} />
        {chatMinimized ? 'Show chat' : 'Hide chat'}
      </ToggleChatButton>
      <PromptFooter
        inputRef={inputRef}
        value={controls.prompt}
        onChange={(e) => controls.setPrompt(e.target.value)}
        onKeyDown={controls.onKeyDown}
        isLoading={controls.isStreaming}
        onSend={controls.handleSend}
        placeholder={PLACEHOLDER}
        sendDisabled={controls.sendDisabled}
        showSpinner={controls.isReportLoading}
        statusText={controls.loadingStatusText}
        onFocus={chatMinimized ? onExpandChat : undefined}
      />
    </>
  );
};

type ReportWithChatProps = {
  report: Object,
  reportConfig: Object,
  resolvedReportConfig: Object,
  topLevelConfig: Object,
  allLocationsList: Array<Object>,
  availableMetrics: Object,
  startOfWeek: 'monday' | 'sunday',
  showStaffFilters: boolean,
  fetchingQueryData: boolean,
  history: Object,
  dispatch: Function,
  organisationName: string,
  // Callbacks from parent
  updateReportConfig: (args: Object) => void,
  handleTopLevelExcludeStaff: () => void,
  openScheduledReportJobExplorer: () => void,
  // Top bar action handlers
  canShowEdit: boolean,
  canShowShare: boolean,
  canShowDelete: boolean,
  onEdit: () => void,
  onShare: () => void,
  onDelete: () => void,
  perms: Object,
  onPermissionsChange: (perms: Object) => void,
  // Modals
  showDeleteConfirm: boolean,
  showShareConfirm: boolean,
  confirmDelete: () => Promise<void>,
  confirmShare: () => Promise<void>,
  setShowDeleteConfirm: (show: boolean) => void,
  setShowShareConfirm: (show: boolean) => void,
  // Other
  allReports: Array<Object>,
  isScheduledReportJobExplorerOpen: boolean,
  closeScheduledReportJobExplorer: () => void,
  // Functions computed in SingleReportPage.render()
  getHierarchyConfig: (parent: Object, child: Object) => Object,
  transformHierarchyConfig: (config: Object) => Object,
  isOrgReports: boolean,
  staffDetectionDisabled: boolean,
  demographicsDisabled: boolean,
  availableMetricGroups: string[],
};

const ReportWithChat = ({
  report,
  topLevelConfig,
  allLocationsList,
  availableMetrics,
  startOfWeek,
  showStaffFilters,
  fetchingQueryData,
  history,
  dispatch,
  organisationName,
  // Callbacks from parent
  updateReportConfig,
  handleTopLevelExcludeStaff,
  openScheduledReportJobExplorer,
  // Top bar action handlers
  canShowEdit,
  canShowShare,
  canShowDelete,
  onEdit,
  onShare,
  onDelete,
  perms,
  onPermissionsChange,
  // Modals
  showDeleteConfirm,
  showShareConfirm,
  confirmDelete,
  confirmShare,
  setShowDeleteConfirm,
  setShowShareConfirm,
  // Other
  allReports,
  isScheduledReportJobExplorerOpen,
  closeScheduledReportJobExplorer,
  // Functions computed in SingleReportPage.render()
  getHierarchyConfig,
  transformHierarchyConfig,
  isOrgReports,
  staffDetectionDisabled,
  demographicsDisabled,
  availableMetricGroups,
}: ReportWithChatProps): React$Node => {
  const [threadId, setThreadId] = useState(() => uuidv4());
  const [chatMinimized, setChatMinimized] = useState(false);
  const inputRef = useRef<?HTMLTextAreaElement>(null);
  const footerRef = useRef<?HTMLDivElement>(null);
  const controllerResetRef = useRef<?Function>(null);
  // Track the last report id we've fully initialized against.
  // Start as null so the first real report.id assignment doesn't trigger a reset.
  const prevReportIdRef = useRef<?string>(null);
  
  const reportContext = useReportRenderContext();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Generate new thread when report changes (not on initial mount)
  useEffect(() => {
    // Ignore until we have a real report id
    if (!report || !report.id) return;

    // First time we see a report.id, just record it (don't reset)
    if (prevReportIdRef.current === null) {
      prevReportIdRef.current = report.id;
      return;
    }

    // Only run when report.id actually changes
    if (prevReportIdRef.current === report.id) return;
    prevReportIdRef.current = report.id;

    setThreadId(uuidv4());
    setChatMinimized(false);
    // Reset chat when report changes
    if (controllerResetRef.current) {
      controllerResetRef.current();
    }
    // Reset lastSentSignature so markdown is sent in new chat
    if (reportContext?.setLastSentSignature) {
      reportContext.setLastSentSignature(null);
    }
    // Focus the prompt input when navigating between reports
    requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    });
  }, [report.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Don't auto-focus on mount — chat starts collapsed and focusing would expand it

  // Refresh thread list on unmount
  useEffect(() => {
    return () => {
      dispatch(fetchChatThreads());
    };
  }, [dispatch]);

  const handleFirstInteraction = useCallback(() => {
    if (chatMinimized) {
      setChatMinimized(false);
    }
  }, [chatMinimized]);

  const handleToggleMinimize = useCallback(() => {
    setChatMinimized((prev) => !prev);
  }, []);

  const handleExpandChat = useCallback(() => {
    setChatMinimized(false);
  }, []);

  const handleNewChat = useCallback(() => {
    setThreadId(uuidv4());
    
    // Reset chat content
    if (controllerResetRef.current) {
      controllerResetRef.current();
    }
    
    // Reset lastSentSignature so markdown is sent with next prompt
    if (reportContext?.setLastSentSignature) {
      reportContext.setLastSentSignature(null);
    }
    
    dispatch(fetchChatThreads());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Report content component
  const reportContent = (
    <ReportContent
      report={report}
      topLevelConfig={topLevelConfig}
      allLocationsList={allLocationsList}
      availableMetrics={availableMetrics}
      startOfWeek={startOfWeek}
      showStaffFilters={showStaffFilters}
      fetchingQueryData={fetchingQueryData}
      history={history}
      updateReportConfig={updateReportConfig}
      handleTopLevelExcludeStaff={handleTopLevelExcludeStaff}
      openScheduledReportJobExplorer={openScheduledReportJobExplorer}
      canShowEdit={canShowEdit}
      canShowShare={canShowShare}
      canShowDelete={canShowDelete}
      onEdit={onEdit}
      onShare={onShare}
      onDelete={onDelete}
      onNewChat={handleNewChat}
      perms={perms}
      onPermissionsChange={onPermissionsChange}
      showDeleteConfirm={showDeleteConfirm}
      showShareConfirm={showShareConfirm}
      confirmDelete={confirmDelete}
      confirmShare={confirmShare}
      setShowDeleteConfirm={setShowDeleteConfirm}
      setShowShareConfirm={setShowShareConfirm}
      allReports={allReports}
      isScheduledReportJobExplorerOpen={isScheduledReportJobExplorerOpen}
      closeScheduledReportJobExplorer={closeScheduledReportJobExplorer}
      getHierarchyConfig={getHierarchyConfig}
      transformHierarchyConfig={transformHierarchyConfig}
      isOrgReports={isOrgReports}
    />
  );

  // Chat window content (only shown when expanded)
  const chatContent = (
    <ChatContainer>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', width: '100%' }}>
        <ChatWindow />
      </div>
    </ChatContainer>
  );

  const orgContextValue = useMemo(() => ({ organisationName }), [organisationName]);

  return (
    <OrganisationContext.Provider value={orgContextValue}>
      <ReportChatWrapper
      threadId={threadId}
      reportRenderContext={reportContext}
      staffDetectionDisabled={staffDetectionDisabled}
      demographicsDisabled={demographicsDisabled}
      availableMetricGroups={availableMetricGroups}
    >
      <PageContainer>
        <SplitArea>
          {/*
            Always render the split layout so the report widgets stay mounted.
            Toggling chat visibility previously caused report widgets to unmount/remount,
            which churned widget state and could change markdown signatures between prompts.
          */}
          <ResizableSplit
            initialSplit={0.59}
            min={0.15}
            max={0.92}
            collapsed={chatMinimized}
            minBottomHeight={0}
            hideDividerWhenCollapsed
            top={
              <div style={{ flex: '1 1 auto', overflow: 'auto', width: '100%' }}>
                {reportContent}
              </div>
            }
            bottom={chatContent}
          />
        </SplitArea>

        {/* Prompt footer - always visible at the bottom */}
        <FooterArea ref={footerRef}>
          <PromptFooterWithContext
            inputRef={inputRef}
            onFirstInteraction={handleFirstInteraction}
            controllerResetRef={controllerResetRef}
            chatMinimized={chatMinimized}
            onToggleMinimize={handleToggleMinimize}
            onExpandChat={handleExpandChat}
            footerRef={footerRef}
          />
        </FooterArea>
      </PageContainer>
    </ReportChatWrapper>
    </OrganisationContext.Provider>
  );
};

const mapStateToProps = (state) => ({
  organisationName: Selectors.getOrganisationName(state),
  staffDetectionDisabled: !Selectors.userHasFeature(ORG_HAS_STAFF_DETECTION, state),
  demographicsDisabled: Selectors.userHasFeature(DISABLE_AGE_GENDER, state),
  availableMetricGroups: Selectors.getAvailableMetricGroups(state),
});

export default connect(mapStateToProps)(ReportWithChat);
